/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { factoryT, factoryTBuilder, fields } from 'factory-t';
import {
    CommittentInfo,
    CorrectionDocumentResource,
    CreditPayDocumentResource,
    DocType,
    DocumentContentBase,
    DocumentResource,
    DocumentsListItemDto,
    GasStationInfo,
    ProductItemInfo,
    MoneyBackDocumentResource,
    OpenShiftDocumentResource,
    PaymentTypes,
    PrepayDocumentResource,
    QuantityTypes,
    RollBackDocumentResource,
    SaleDocumentResource,
    VatAmounts,
    ZReportCurrency,
    ZReportDocumentResource,
    InterbankTransactions,
    TransactionType,
    ItemCodeTypes,
} from './documentsClient.typings';
import { Currency } from '../apiCommon.typings';
import moment from 'moment';
import { DATE_TIME_WITH_MS_FORMAT } from '../../../utils/utils';

export const documentListItemDtoFactory = factoryTBuilder<DocumentsListItemDto>({
    documentId: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5e',
    documentIdHash: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5e_hash',
    docTypeId: fields.sequence([
        DocType.Sale,
        DocType.MoneyBack,
        DocType.RollBack,
        DocType.Correction,
        DocType.Prepayment,
        DocType.CreditPay,
        DocType.OpenShift,
        DocType.ZReport,
    ]),
    docNumber: (ctx) => (ctx.index < 4 ? null : 25 + ctx.index),
    createdAtUtc: (ctx) => moment().subtract(ctx.index, 'seconds').utc().format(DATE_TIME_WITH_MS_FORMAT),
    companyTaxNumber: (ctx) => `company_taxNumber_${ctx.index}`,
    storeTaxNumber: (ctx) => `store_taxNumber_${ctx.index}`,
    cashBoxTaxNumber: (ctx) => `7980898980${ctx.index}`,

    sum: 33.55555,
    taxSum: 21.8,
    taxRates: ['0', '8', '18', '2', 'OTHER'],
    paymentTypes: fields.sequence([['PREPAYMENT', 'CASHLESS'], ['CASH', 'BONUS', 'CREDIT'], null]),
    parentDocumentId: '4WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5e',
    periodFrom: '2021-11-27T01:01:00.000Z',
    periodTo: '2021-11-29T01:01:00.000Z',
    numberOfDocs: 58,
    restored: fields.sequence([true, false, null, undefined]),
}).factory();

export const documentsGeneralInfoFactory = factoryT<Omit<DocumentResource, 'content'>>({
    storeAddress: 'AZ1065 BAKI ŞƏHƏRİ YASAMAL RAYONU MURTUZA MUXTAROV ev.188 m.33',
    storeTaxNumber: '150453397113001',
    storeName: 'OFİS',
    storePhone: '',
    companyName: '"OMNITECH" MƏHDUD MƏSULİYYƏTLİ CƏMİYYƏTİ',
    companyTaxNumber: '1504533971',
    documentId: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5e',
    shortDocumentId: 'HHJ2NthMYHdN',
    cashregisterModelName: 'TEST_02',
    cashregisterFactoryNumber: 'all',
    cashregisterModelTaxNumber: '1234567',
    createdAtUtc: '2024-03-18T15:49:40',
    insertedAtUtc: '2024-03-18T15:59:40',
    isIncorrectTime: true,
});

export const paymentTypesFactory = factoryT<PaymentTypes>({
    cashSum: 10,
    cashlessSum: 25,
    creditSum: 990,
    bonusSum: 18,
    prepaymentSum: 15,
    incomingSum: 50,
    changeSum: 40,
});

export const documentBaseFactory = factoryT<DocumentContentBase>({
    cashboxTaxNumber: (ctx) => `7323432${ctx.index}`,
    docNumber: 681,
    createdAtUtc: 1617254301111,
    cashier: 'SuperApi',
    currency: Currency.AZN,
    documentVersion: 292,
    globalDocNumber: 3120,
    bonusCardNumber: '73234673244',
    restored: true,
});

export const interbankTransactionsFactory = factoryT<InterbankTransactions>({
    rrn: '1e3q2e34rb3c2555',
    transactions: [
        {
            rrn: '1e3q2e34rb3c2555',
            amount: 23.3,
        },
        {
            rrn: '1e3q2e34rb3c2555',
            amount: 3.34,
            type: TransactionType.REVERSE,
            approval_code: '111111',
        },
        {
            rrn: '1e3q2e34rb3c2555',
            amount: 0,
            type: TransactionType.REFUND,
            approval_code: '222222',
        },
        {
            rrn: '1e3q2e34rb3c2555',
            amount: 700,
            type: TransactionType.PURCHASE,
            approval_code: '333333',
        },
        {
            rrn: '1e3q2e34rb3c2559xxxxxxxx',
            amount: 0.869,
            type: TransactionType.PURCHASE,
            approval_code: '123456xxxxxxxx',
        },
    ],
});

export const itemsFactory = factoryT<ProductItemInfo>({
    itemNumber: (ctx) => ctx.index,
    itemCode: '5123',
    itemCodeType: ItemCodeTypes.DUNS,
    itemPrice: 100,
    itemSum: 1000,
    itemVatPercent: 18,
    itemQuantity: 10,
    itemQuantityType: QuantityTypes.Things,
    itemVatAmount: 21.0,
    itemName: fields.sequence(['Çiken\n\tSpaysi\r\n\tMenyu']),
    //itemMarkingCode: 'hy76fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
    itemMarkingCodes: [
        '1116fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
        '2226fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
        '3336fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
        '4446fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
        '5556fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
        '6666fhjkoputrt6789hjklytredz3456789rtyfg4567dfgjhg8765cvbnfde234',
    ],
    itemCreditpaySum: null, //56.699,
    itemAgentCommission: 15,
});

export const vatAmountsFactory = factoryT<VatAmounts>({
    vatPercent: (ctx) => ctx.index - 1,
    vatSum: 10,
    vatResult: 1.53,
});

export const vatAmountsFactoryNotTaxable = factoryT<VatAmounts>({
    vatPercent: 0,
    vatSum: 10,
    vatResult: 1.53,
});

export const vatAmountsFactoryFull = factoryT<VatAmounts>({
    vatPercent: (ctx) => ctx.index - 1,
    vatSum: 10,
    vatResult: (ctx) => (ctx.index - 1) / 10,
});

export const gasStationInfoFactory = factoryT<GasStationInfo>({
    fdp: '12as2451zg2as5f35sa937as68dwg642212c52',
    pump: '31dgaxbb6k9vl0vpb6031',
    gun: '25125612324125152',
    reservoir: '725124',
});

export const committentInfoFactory = factoryT<CommittentInfo>({
    tin: '200494716111001',
    name: 'Jon Doe',
    agentContract: '25125612324125152',
    agentCommission: 15,
});

export const saleDocumentResource = factoryT<SaleDocumentResource>({
    ...paymentTypesFactory.item(),
    ...documentBaseFactory.item(),
    ...interbankTransactionsFactory.item(),

    docType: DocType.Sale,
    sum: 10,
    items: itemsFactory.list({ count: 10 }),
    itemsHash: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5eer4325rfsghbvft76543',
    vatAmounts: vatAmountsFactory.list({ count: 5 }),
    positionInShift: 1,
    //uncomment for SaleWithPrepay
    parentDocument: '2WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5e',
    //uncomment for SaleCredit
    creditContract: 'AAA321cg24',
    creditPayer: 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit.',
    carNumber: '10 GG 887',
    committentInfo: committentInfoFactory.item(),
    gasStationInfo: gasStationInfoFactory.item(),
    guestsCount: 21,
});

export const moneyBackDocumentResource = factoryT<MoneyBackDocumentResource>({
    ...paymentTypesFactory.item(),
    ...documentBaseFactory.item(),
    ...interbankTransactionsFactory.item(),

    parentDocument: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG9Ky5e',
    items: itemsFactory.list({ count: 3 }),
    itemsHash: 'PKxy0YEeWI7t/U4tym0cEQ==',
    sum: 212,
    docType: DocType.MoneyBack,
    vatAmounts: vatAmountsFactory.list({ count: 1 }),
    positionInShift: 12,
    moneyBackType: DocType.Prepayment, //DocType.Sale, DocType.Prepayment, DocType.CreditPay
    carNumber: '10 GG 887',
    committentInfo: committentInfoFactory.item(),
    gasStationInfo: gasStationInfoFactory.item(),
});

export const rollBackDocumentResource = factoryT<RollBackDocumentResource>({
    ...paymentTypesFactory.item(),
    ...documentBaseFactory.item(),
    ...interbankTransactionsFactory.item(),

    sum: 312,
    parentDocument: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG9Ky5e',
    docType: DocType.RollBack,
    vatAmounts: vatAmountsFactory.list({ count: 1 }),
    positionInShift: 12,
    parentDocumentType: DocType.Sale,
    committentInfo: committentInfoFactory.item(),
});

export const correctionDocumentResource = factoryT<CorrectionDocumentResource>({
    ...paymentTypesFactory.item(),
    ...documentBaseFactory.item(),
    ...interbankTransactionsFactory.item(),

    items: itemsFactory.list({ count: 0 }),
    itemsHash: 'PKxy0YEeWI7t/U4tym0cEQ==',
    vatAmounts: vatAmountsFactory.list({ count: 1 }),
    docType: DocType.Correction,
    sum: 235,
    firstOperationAtUtc: 1615274301,
    lastOperationAtUtc: 1615274301,
    positionInShift: 10,
    gasStationInfo: gasStationInfoFactory.item(),
    committentInfo: committentInfoFactory.item(),
});

export const prepayDocumentResource = factoryT<PrepayDocumentResource>({
    ...paymentTypesFactory.item(),
    ...documentBaseFactory.item(),
    ...interbankTransactionsFactory.item(),

    docType: DocType.Prepayment,
    items: itemsFactory.list({ count: 1 }),
    itemsHash: 'PKxy0YEeWI7t/U4tym0cEQ==',
    vatAmounts: vatAmountsFactory.list({ count: 1 }),
    committentInfo: committentInfoFactory.item(),
    sum: 10,
    positionInShift: 35,
});

export const creditPayDocumentResource = factoryT<CreditPayDocumentResource>({
    ...paymentTypesFactory.item(),
    ...documentBaseFactory.item(),
    ...interbankTransactionsFactory.item(),

    docType: DocType.CreditPay,
    items: itemsFactory.list({ count: 3 }),
    itemsHash: 'PKxy0YEeWI7t/U4tym0cEQ==',
    vatAmounts: vatAmountsFactory.list({ count: 1 }),
    sum: 10,
    positionInShift: 35,
    parentDocument: '8WY4UxTPCcdsvfgrbfvdccadsvfghfvcxcdsvfbdghbvcdsafg',
    creditContract: 'AAA321cJfEm',
    creditPayer:
        'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa,  sed do eiusmod tempor incididunt ut labore et dolore aliqua.',
    residue: 980,
    paymentNumber: 2,
    committentInfo: committentInfoFactory.item(),
});

export const openShiftDocumentResource = factoryT<OpenShiftDocumentResource>({
    docType: DocType.OpenShift,
    reportNumber: 12,
    createdAtUtc: 1615274301,
    cashboxTaxNumber: '73234325',
    documentVersion: 1,
    globalDocNumber: 67,
    restored: fields.sequence([true, false, null]),
});

export const zReportCurrency = factoryT<ZReportCurrency>({
    currency: Currency.AZN,

    turnover: 1220,
    turnoverTax: 98,

    correctionSum: 10045,
    correctionCount: 100,
    correctionCashSum: 202,
    correctionCashlessSum: 201,
    correctionPrepaymentSum: 204,
    correctionCreditSum: 205,
    correctionBonusSum: 203,

    moneyBackCount: 0,
    moneyBackCashlessSum: 0,
    moneyBackCashSum: 0,
    moneyBackPrepaymentSum: 0,
    moneyBackCreditSum: 0,
    moneyBackBonusSum: 0,
    moneyBackSum: 0,

    rollbackSum: 10,
    rollbackCount: 10,
    rollbackCashlessSum: 2,
    rollbackCashSum: 2,
    rollbackPrepaymentSum: 2,
    rollbackCreditSum: 2,
    rollbackBonusSum: 2,

    saleCount: 100,
    saleCashSum: 500,
    saleSum: 100,
    saleCashlessSum: 100,
    saleBonusSum: 100,
    saleCreditSum: 10,
    salePrepaymentSum: 100,

    prepayCount: 200,
    prepaySum: 2000,
    prepayCashSum: 400,
    prepayCashlessSum: 400,
    prepayPrepaymentSum: 400,
    prepayCreditSum: 400,
    prepayBonusSum: 400,

    creditpayCount: 30,
    creditpaySum: 3000,
    creditpayCashSum: 700,
    creditpayCashlessSum: 700,
    creditpayPrepaymentSum: 700,
    creditpayCreditSum: 700,
    creditpayBonusSum: 700,

    saleVatAmounts: vatAmountsFactoryFull.list({ count: 5 }),
    moneyBackVatAmounts: [], //vatAmountsFactory.list({ count: 1 }),
    rollbackVatAmounts: vatAmountsFactoryNotTaxable.list({ count: 5 }),
    correctionVatAmounts: vatAmountsFactoryFull.list({ count: 3 }),
    prepayVatAmounts: vatAmountsFactoryFull.list({ count: 2 }),
    creditpayVatAmounts: vatAmountsFactoryFull.list({ count: 2 }),
});

export const zReportDocumentResource = factoryT<ZReportDocumentResource>({
    docType: DocType.ZReport,
    currencies: zReportCurrency.list({ count: 1 }),
    cashboxTaxNumber: 'test_00023',
    shiftOpenAtUtc: 1615274301,
    createdAtUtc: 1615278866,
    firstDocNumber: 8,
    lastDocNumber: undefined,
    reportNumber: 84,
    docCountToSend: 0,
    currency: Currency.AZN,
    documentVersion: 292,
    globalDocNumber: 3120,
    restored: fields.sequence([true, false, null]),
});
